import React, { useContext } from 'react';
import { Chip, Spinner, Tooltip } from 'design-system';
import { useObjectPreview } from 'corp/hooks/smartId/useObjectPreview';
import { INamedObjectTypes, ISubtaskPreviewJson } from '@scale/shared/types/lookup';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import TaskShortcuts from 'corp/components/Lookup/sections/subsections/task/shortcuts';
import moment from 'moment';
import { lidarTypes, ReviewLevel } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import RemoAdminContext from 'components/remoAdmin/remoAdminContext';
import { CustomQuickAction } from './SmartLink';

const SubtaskPreview: React.FC<{ id: string; customQuickAction?: CustomQuickAction }> = ({
  id,
  customQuickAction,
}) => {
  const remoAdminCtx = useContext(RemoAdminContext);
  const {
    value: subtask,
    loading,
    error,
  } = useObjectPreview<ISubtaskPreviewJson>(id, INamedObjectTypes.Subtask);
  if (error) return <span>Sorry, there was an error</span>;
  if (loading) return <Spinner />;

  const reviewLevelDisplay = ((reviewLevel: ReviewLevel) =>
    reviewLevel === -1 ? 'Attempt' : 'L' + reviewLevel)(subtask!.reviewLevel);
  const isLidar = (lidarTypes as unknown as TaskType[]).includes(subtask!.type);
  const isPending = subtask!.status === 'pending';
  const isComplete = subtask!.status === 'completed';
  const customerId = subtask!.user?._id;
  const customerEmail = subtask!.user?.email;
  const basePathname = window.location.href.includes('/expert')
    ? 'expert/outlieradmin'
    : 'remoadmin';

  const subtaskHRef = remoAdminCtx.isRemoAdmin
    ? `/${basePathname}/tools/lookup/${id}`
    : `/lookup/${id}`;
  const projectHRef = remoAdminCtx.isRemoAdmin
    ? `/${basePathname}/tools/lookup/${subtask?.project?._id}`
    : `/qm/queue/${subtask?.project?._id}`;

  return (
    <div className="subtask-preview">
      <style jsx>{`
        .subtask-preview__banner {
          font-weight: bold;
          padding: 10px;
          display: flex;
          align-items: center;
        }

        .subtask-preview__thing-list > div {
          display: flex;
        }

        .subtask-preview__thing-list strong {
          color: #666;
          margin-right: 10px;
          min-width: 120px;
          text-align: right;
        }
      `}</style>
      <div className="subtask-preview__banner">
        <Chip size="small" label={reviewLevelDisplay} />
        <h3 style={{ margin: '0 5px' }}>{id}</h3>
      </div>
      <div className="subtask-preview__body">
        <div className="subtask-preview__thing-list">
          <div>
            <strong>Lookup</strong>
            <IsomorphicLink target="_blank" rel="noopener noreferrer" href={subtaskHRef}>
              {id}{' '}
            </IsomorphicLink>
          </div>
          {!remoAdminCtx.isRemoAdmin && (
            <div>
              <strong>Project</strong>
              <IsomorphicLink target="_blank" rel="noopener noreferrer" href={projectHRef}>
                {subtask?.project?.name}{' '}
              </IsomorphicLink>
            </div>
          )}
          {!!subtask?.created_at && (
            <div>
              <strong>Created</strong>
              <Tooltip placement={'top'} title={new Date(subtask?.created_at).toLocaleString()}>
                <span>{moment(subtask?.created_at).fromNow()}</span>
              </Tooltip>
            </div>
          )}
          <div>
            <strong>Quick Actions</strong>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <TaskShortcuts
                isLidar={isLidar}
                id={id}
                type="subtask"
                isPending={isPending}
                isComplete={isComplete}
                customerEmail={customerEmail}
                customerId={customerId}
                customQuickAction={customQuickAction}
                user={customerId}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubtaskPreview;
